import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
export const API_BASE = `${BACKEND_URL}/api`;

// Configure axios defaults
axios.defaults.withCredentials = true;

export const AuthProvider = ({ children }) => {
  // authState: null (loading), false (unauthenticated), object (user)
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(() => localStorage.getItem('qinex_auth_token') || null);
  const [loading, setLoading] = useState(true);

  const fetchCurrentUser = useCallback(async (authToken) => {
    try {
      const headers = {};
      const activeToken = authToken || token || localStorage.getItem('qinex_auth_token');
      if (activeToken) {
        headers['Authorization'] = `Bearer ${activeToken}`;
      }
      const response = await axios.get(`${API_BASE}/auth/me`, { headers });
      setUser(response.data);
      return response.data;
    } catch (error) {
      console.warn("User not authenticated or session expired:", error.response?.data?.detail || error.message);
      setUser(false);
      localStorage.removeItem('qinex_auth_token');
      setToken(null);
      return null;
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    const savedToken = localStorage.getItem('qinex_auth_token');
    fetchCurrentUser(savedToken);
  }, [fetchCurrentUser]);

  const login = async (username, password) => {
    try {
      const response = await axios.post(`${API_BASE}/auth/login`, {
        username: username.trim(),
        password: password
      });
      const { user: userData, access_token } = response.data;
      if (access_token) {
        localStorage.setItem('qinex_auth_token', access_token);
        setToken(access_token);
      }
      setUser(userData);
      return { success: true, user: userData, message: response.data.message };
    } catch (error) {
      const detail = error.response?.data?.detail;
      let errorMsg = "Login failed. Please check your credentials.";
      if (typeof detail === 'string') {
        errorMsg = detail;
      } else if (Array.isArray(detail)) {
        errorMsg = detail.map(d => d.msg || JSON.stringify(d)).join(', ');
      } else if (detail?.msg) {
        errorMsg = detail.msg;
      }
      return { success: false, error: errorMsg, status: error.response?.status };
    }
  };

  const setupAdmin = async (name, username, password) => {
    try {
      const response = await axios.post(`${API_BASE}/auth/setup-admin`, {
        name: name.trim(),
        username: username.trim(),
        password: password
      });
      const { user: userData, access_token } = response.data;
      if (access_token) {
        localStorage.setItem('qinex_auth_token', access_token);
        setToken(access_token);
      }
      setUser(userData);
      return { success: true, user: userData, message: response.data.message };
    } catch (error) {
      const detail = error.response?.data?.detail;
      let errorMsg = "Initial setup failed.";
      if (typeof detail === 'string') {
        errorMsg = detail;
      } else if (Array.isArray(detail)) {
        errorMsg = detail.map(d => d.msg || JSON.stringify(d)).join(', ');
      }
      return { success: false, error: errorMsg };
    }
  };

  const logout = async () => {
    try {
      await axios.post(`${API_BASE}/auth/logout`, {}, {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
    } catch (err) {
      console.warn("Logout request failed:", err);
    } finally {
      localStorage.removeItem('qinex_auth_token');
      setToken(null);
      setUser(false);
    }
  };

  const getAuthHeader = () => {
    const activeToken = token || localStorage.getItem('qinex_auth_token');
    return activeToken ? { Authorization: `Bearer ${activeToken}` } : {};
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        token,
        loading,
        login,
        logout,
        setupAdmin,
        refreshUser: () => fetchCurrentUser(token),
        getAuthHeader,
        isAuthenticated: !!user && user !== false,
        isAdmin: user?.role === 'admin',
        isAccountant: user?.role === 'accountant',
        isReception: user?.role === 'reception',
        isLabStaff: user?.role === 'lab_staff',
        isPharmacist: user?.role === 'pharmacist',
        isManagement: user?.role === 'management',
        isManager: user?.role === 'manager' || user?.role === 'management',
        isMarketingExecutive: user?.role === 'marketing_executive',
        marketingExecutiveId: user?.marketing_executive_id || null,
        canUpdatePayments: user?.role === 'admin' || user?.role === 'accountant',
        canCreateReferral: ['admin', 'staff', 'reception'].includes(user?.role),
        canEditReferral: ['admin', 'lab_staff', 'pharmacist', 'reception'].includes(user?.role),
        canCalculateReferral: ['admin', 'manager', 'management'].includes(user?.role),
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
